import { useCallback, useEffect, useRef, useState } from 'react';
import API_ENDPOINTS from 'utils/api/endpoints';

const HEARTBEAT_INTERVAL_MS = 4000;
const BLUR_CLEAR_DELAY_MS = 500;

const cellKey = cell =>
  [
    cell?.detail_sheet_type || '',
    cell?.row_id || '',
    cell?.column_key || '',
  ].join(':');

const selectedCellsAreEqual = (current = [], next = []) => {
  if (current.length !== next.length) return false;

  const currentKeys = current.map(cellKey).sort();
  const nextKeys = next.map(cellKey).sort();
  return currentKeys.every((key, index) => key === nextKeys[index]);
};

export const useCellPresence = ({
  budgetId,
  detailSheetType,
  enabled,
  presences = [],
}) => {
  const [activeCell, setActiveCell] = useState(null);
  const activeCellRef = useRef(null);
  const heartbeatTimerRef = useRef(null);
  const blurClearTimerRef = useRef(null);
  const sharedPresences = Array.isArray(presences) ? presences : [];

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  const sendHeartbeat = useCallback(
    async cell => {
      if (!enabled || !budgetId || !cell?.rowId || !cell?.columnKey) return;

      const payload = {
        budget: budgetId,
        detail_sheet_type: cell.detailSheetType || detailSheetType,
        row_id: cell.rowId,
        column_key: cell.columnKey,
        row_label: cell.rowLabel || '',
        selected_cells: Array.isArray(cell.selectedCells)
          ? cell.selectedCells
          : [],
      };

      try {
        await API_ENDPOINTS.budgets.update_cell_presence(payload);
      } catch (error) {
        stopHeartbeat();
      }
    },
    [budgetId, detailSheetType, enabled, stopHeartbeat]
  );

  const startCellPresence = useCallback(
    cell => {
      if (!enabled || !budgetId || !cell?.rowId || !cell?.columnKey) return;

      if (blurClearTimerRef.current) {
        clearTimeout(blurClearTimerRef.current);
        blurClearTimerRef.current = null;
      }

      activeCellRef.current = cell;
      setActiveCell(cell);
      sendHeartbeat(cell);
      stopHeartbeat();
      heartbeatTimerRef.current = setInterval(() => {
        if (activeCellRef.current) {
          sendHeartbeat(activeCellRef.current);
        }
      }, HEARTBEAT_INTERVAL_MS);
    },
    [budgetId, enabled, sendHeartbeat, stopHeartbeat]
  );

  const clearCellPresence = useCallback(async () => {
    const hadActiveCell = Boolean(activeCellRef.current);
    activeCellRef.current = null;
    setActiveCell(null);
    stopHeartbeat();

    if (!budgetId || !hadActiveCell) return;

    try {
      await API_ENDPOINTS.budgets.clear_cell_presence(budgetId);
    } catch (error) {
      // Presence will expire on the backend shortly if this request is interrupted.
    }
  }, [budgetId, stopHeartbeat]);

  const updateActiveSelectedCells = useCallback(
    selectedCells => {
      if (!activeCellRef.current) return;

      const nextSelectedCells = Array.isArray(selectedCells)
        ? selectedCells
        : [];
      if (
        selectedCellsAreEqual(
          activeCellRef.current.selectedCells || [],
          nextSelectedCells
        )
      ) {
        return;
      }

      const nextCell = {
        ...activeCellRef.current,
        selectedCells: nextSelectedCells,
      };
      activeCellRef.current = nextCell;
      setActiveCell(nextCell);
      sendHeartbeat(nextCell);
    },
    [sendHeartbeat]
  );

  const scheduleClearCellPresence = useCallback(() => {
    if (blurClearTimerRef.current) {
      clearTimeout(blurClearTimerRef.current);
    }
    blurClearTimerRef.current = setTimeout(() => {
      clearCellPresence();
    }, BLUR_CLEAR_DELAY_MS);
  }, [clearCellPresence]);

  useEffect(() => {
    return () => {
      if (blurClearTimerRef.current) {
        clearTimeout(blurClearTimerRef.current);
      }
      clearCellPresence();
    };
  }, [clearCellPresence]);

  return {
    presences: enabled ? sharedPresences : [],
    activeCell,
    startCellPresence,
    scheduleClearCellPresence,
    updateActiveSelectedCells,
  };
};
